import { clearPersistentMapCaches } from "../../services/amapCacheReset";
import { useEffect, useRef, useState } from "react";
import { KeyRound, Save } from "lucide-react";
import { ActionButton } from "../design-system/ActionButton";
import { DialogShell } from "../design-system/DialogShell";
import { defaultModelSettings, loadModelSettings, saveModelSettings } from "../../logic/modelClient";
import type { ModelSettings } from "../../types/domain";

export function SettingsModal({
  open,
  onClose,
  onSaved
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (settings: ModelSettings) => void;
}) {
  const [settings, setSettings] = useState<ModelSettings>(defaultModelSettings);
  const originalProxyUrl = useRef("");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSaveError(null);
      loadModelSettings().then((loaded) => {
        originalProxyUrl.current = loaded.amapProxyUrl ?? "";
        setSettings(loaded);
      });
    }
  }, [open]);

  if (!open) return null;

  async function handleSave() {
    try {
      setSaveError(null);
      const saved = await saveModelSettings(settings);
      const mapChanged = Boolean(window.geelyDemo?.requestAmap) && (saved.amapProxyUrl ?? "") !== originalProxyUrl.current;
      onSaved(saved);
      onClose();
      if (mapChanged) {
        try { clearPersistentMapCaches(localStorage); } catch { /* Storage may be disabled. */ }
        window.location.reload();
      }
    } catch {
      setSaveError("保存失败，请检查地图网关地址后重试。请使用 HTTPS 地址或本机回环地址，路径以 /amap 结尾。");
    }
  }

  return (
    <DialogShell
      ariaLabel="隐藏模型设置"
      className="settings-modal"
      closeLabel="关闭设置"
      onClose={onClose}
      showCloseButton
    >
      <div className="settings-modal__title">
        <KeyRound size={24} />
        <div>
          <h2>隐藏模型设置</h2>
          <p>Windows 或 Mac 桌面版可填 key 启用语义理解和真实文案；失败会自动回退本地规则。</p>
        </div>
      </div>
      <label className="settings-modal__toggle">
        <span>启用模型语义/文案</span>
        <input
          type="checkbox"
          checked={settings.modelEnabled}
          onChange={(event) => setSettings((current) => ({ ...current, modelEnabled: event.target.checked }))}
        />
      </label>
      <label>
        <span>API Key</span>
        <input
          type="password"
          value={settings.apiKey}
          onChange={(event) => setSettings((current) => ({ ...current, apiKey: event.target.value }))}
          placeholder="sk-..."
        />
      </label>
      <label>
        <span>Base URL</span>
        <input
          value={settings.baseUrl}
          onChange={(event) => setSettings((current) => ({ ...current, baseUrl: event.target.value }))}
        />
      </label>
      <label>
        <span>模型名</span>
        <input
          value={settings.model}
          onChange={(event) => setSettings((current) => ({ ...current, model: event.target.value }))}
        />
      </label>
      {window.geelyDemo?.requestAmap ? (
        <label>
          <span>地图网关地址（桌面版）</span>
          <input
            value={settings.amapProxyUrl ?? ""}
            onChange={(event) => setSettings((current) => ({ ...current, amapProxyUrl: event.target.value }))}
            placeholder="https://your-demo-host/amap"
          />
          <small>填入已部署的地图网关地址。留空使用离线演示；修改地址后保存将刷新页面，使地图配置生效。</small>
        </label>
      ) : null}
      {saveError ? <p role="alert">{saveError}</p> : null}
      <div className="settings-modal__actions">
        <ActionButton icon={<Save size={16} />} variant="primary" onClick={handleSave}>保存</ActionButton>
      </div>
    </DialogShell>
  );
}
